import React from "react";
import { AlertTriangle } from "lucide-react";

import { canalPorChave, temaDoCanal } from "@/components/canais/canais";
import IconeCanal from "@/components/canais/IconeCanal";

import { formatCurrency, formatInt, formatPct } from "../lib/formato";
import BarraResultado, { corMargem } from "./BarraResultado";

function Metrica({ rotulo, valor, detalhe, cor = "" }) {
  return (
    <div className="min-w-0">
      <p className="text-[10px] font-semibold text-muted-foreground uppercase tracking-wider">{rotulo}</p>
      <p className={`text-sm font-bold tabular-nums truncate ${cor}`}>{valor}</p>
      {detalhe && <p className="text-[10px] text-muted-foreground tabular-nums">{detalhe}</p>}
    </div>
  );
}

// Aviso de venda sem ficha técnica: o item entra com custo zero e a margem sobe.
function AvisoSemCusto({ m }) {
  if (!(m.vendas_sem_custo > 0.009)) return null;
  const dica = m.key === "ifood"
    ? "Vincule os itens em Configurações → Sistema → Integração → iFood → Catálogo."
    : "Cadastre a ficha técnica desses produtos.";
  return (
    <div className="mt-3 flex gap-1.5 rounded-lg bg-amber-500/10 border border-amber-500/25 px-2.5 py-1.5 text-[10.5px] leading-snug text-amber-800 dark:text-amber-300" data-e2e="aviso-sem-custo">
      <AlertTriangle className="w-3.5 h-3.5 shrink-0 mt-px" />
      <span><b>{formatCurrency(m.vendas_sem_custo)}</b> em itens sem custo (entram com CMV zero). {dica}</span>
    </div>
  );
}

// Um canal de venda: vendas, CMV, pedidos, ticket, despesas e lucro com margem.
export default function CardModulo({ m, fatTotal }) {
  const canal = canalPorChave(m.key);
  const tema = temaDoCanal(canal);
  const parado = !m.pedidos && !m.despesas;
  const share = fatTotal > 0 ? (m.faturamento / fatTotal) * 100 : 0;
  return (
    <div className={`relative overflow-hidden bg-card rounded-2xl border p-5 ring-1 ${tema.anel} ${parado ? "opacity-60" : "hover:shadow-lg"} transition-shadow`}
      data-e2e={`card-modulo-${m.key}`}>
      <div className={`absolute inset-0 bg-gradient-to-br ${tema.fundo} to-transparent pointer-events-none`} />
      <div className="relative">
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-2.5 min-w-0">
            <div className={`${tema.icone} w-10 h-10 rounded-xl flex items-center justify-center shrink-0`}><IconeCanal canal={canal} /></div>
            <h3 className="font-bold text-base truncate">{canal.nome}</h3>
          </div>
          <span className={`text-xs font-bold px-2 py-1 rounded-md tabular-nums ${tema.icone}`} title="Participação nas vendas do período">{formatPct(share)}</span>
        </div>
        <p className="text-[10px] font-semibold text-muted-foreground uppercase tracking-wider">Vendas</p>
        <p className="text-2xl font-black tabular-nums leading-tight mb-2">{formatCurrency(m.faturamento)}</p>
        {parado ? <p className="text-xs text-muted-foreground italic">Sem vendas nem despesas no período.</p> : (
          <>
            <BarraResultado m={m} />
            <div className="grid grid-cols-2 gap-x-3 gap-y-2.5 mt-3">
              <Metrica rotulo="Pedidos" valor={formatInt(m.pedidos)} />
              <Metrica rotulo="Ticket médio" valor={formatCurrency(m.ticket_medio)} />
              <Metrica rotulo="CMV" valor={formatCurrency(m.cmv)} detalhe={m.cmv_pct != null ? `${formatPct(m.cmv_pct)} das vendas` : null} cor="text-red-600 dark:text-red-400" />
              <Metrica rotulo="Despesas" valor={formatCurrency(m.despesas)} detalhe={m.despesas_qtd ? `${m.despesas_qtd} lançamento(s)` : "nenhuma atribuída"} cor="text-amber-600 dark:text-amber-400" />
            </div>
            <div className="flex items-end justify-between gap-2 mt-3 pt-3 border-t border-border/60">
              <div>
                <p className="text-[10px] font-semibold text-muted-foreground uppercase tracking-wider">Lucro</p>
                <p className={`text-lg font-black tabular-nums ${m.lucro >= 0 ? "text-emerald-600 dark:text-emerald-400" : "text-red-600 dark:text-red-400"}`}>{formatCurrency(m.lucro)}</p>
              </div>
              <span className={`text-xs font-bold px-2 py-1 rounded-md tabular-nums ${corMargem(m.margem_pct)}`} title="Margem = lucro ÷ vendas">
                {m.margem_pct == null ? "sem vendas" : `${formatPct(m.margem_pct)} margem`}
              </span>
            </div>
            <AvisoSemCusto m={m} />
          </>
        )}
      </div>
    </div>
  );
}
